import { useState, useEffect } from "react";
import { getCurrencySymbol, formatTaxPercent, formatAmount } from "../../helper/formatCurrency";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import api from "../../utils/api";

const OrderModal = ({
  open,
  onClose,
  customer,
  productResults,
  onSearchProduct,
  existingItems,
  onSaveOrder,
  loading,
  onSettleBill,
  onCancelAllocation,
}) => {
  const [searchText, setSearchText] = useState("");
  const [items, setItems] = useState([]);
  const { locale, gstFraction, taxLabel } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);
  const gstPercent = gstFraction * 100;
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [openSettleConfirm, setOpenSettleConfirm] = useState(false);

  useEffect(() => {
    if (!open) return;
    setItems(
      (existingItems || []).map((i) => ({
        ...i,
        note: i.note || "",
      })),
    );
    setSearchText("");
  }, [existingItems, open]);

  // Add product to list
  const addItem = (product) => {
    const existing = items.find((i) => i.productId === product._id);
    const priceValue =
      product.offerPrice && product.offerPrice > 0
        ? product.offerPrice
        : product.price;

    if (existing) {
      setItems(
        items.map((i) =>
          i.productId === product._id ? { ...i, quantity: i.quantity + 1 } : i,
        ),
      );
    } else {
      setItems([
        ...items,
        {
          id: product._id,
          name: product.productName,
          price: priceValue,
          productId: product._id,
          quantity: 1,
          note: "",
          status: "queued",
          isTaxable: product.isTaxable === true,
        },
      ]);
    }
    setSearchText("");
  };

  const incrementQty = (id) => {
    setItems(
      items.map((i) => (i.id === id ? { ...i, quantity: i.quantity + 1 } : i)),
    );
  };

  const decrementQty = (id) => {
    setItems(
      items
        .map((i) =>
          i.id === id && i.quantity > 1
            ? { ...i, quantity: i.quantity - 1 }
            : i,
        )
        .filter((i) => i.quantity > 0),
    );
  };

  const removeItem = async (id) => {
    try {
      await api.put(`/orders/customerOrder/${customer.id}/remove-item`, {
        productId: id,
      });

      setItems(items.filter((i) => i.id !== id));

    } catch (err) {
      console.error("Remove failed", err);
    }
  };

  const subtotal = items.reduce(
    (sum, i) => sum + (Number(i.price) || 0) * i.quantity,
    0,
  );

  const taxableSubtotal = items.reduce((sum, i) => {
    if (i.isTaxable !== true) return sum;
    return sum + (Number(i.price) || 0) * i.quantity;
  }, 0);

  const gstAmount = taxableSubtotal * gstFraction;
  const grandTotal = subtotal + gstAmount;

  const handleConfirmCancel = async () => {
    try {
      await onCancelAllocation(customer?.id);
      setOpenCancelConfirm(false);
      onClose();
    } catch (err) {
      console.error("Cancel allocation failed", err);
    }
  };

  const handleConfirmSettle = async () => {
    try {
      await onSettleBill();
      setOpenSettleConfirm(false);
    } catch (err) {
      console.error("Settle failed", err);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-xl shadow-xl w-[95%] max-w-[450px] max-h-[90vh] flex flex-col overflow-hidden mx-4">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4 flex justify-between items-center rounded-t-xl shadow-md">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path
                  fillRule="evenodd"
                  d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                Order for{" "}
                <span className="text-indigo-100">
                  {customer?.displayValue || customer?.name}
                </span>
              </span>
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-indigo-100 transition-colors p-1 hover:bg-white/20 rounded-lg"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="p-6 flex-1 flex flex-col overflow-y-auto">
            {/* Search Product */}
            <div className="relative mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Product
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  onSearchProduct(e.target.value);
                }}
                disabled={loading}
                autoComplete="off"
                placeholder="Search for products..."
              />

              {searchText && productResults?.length > 0 && !loading && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white max-h-[300px] overflow-y-auto z-[1000] shadow-lg rounded-lg border border-gray-200">
                  <div className="py-1">
                    {productResults.map((p) => (
                      <button
                        key={p._id}
                        onClick={() => addItem(p)}
                        className="w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-100 last:border-0"
                      >
                        <div className="flex justify-between items-center w-full">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-800">
                              {p.productName}
                            </div>
                          </div>
                          <strong className="text-sm text-gray-900 ml-3">
                            {currencySymbol}
                            {p.offerPrice && p.offerPrice > 0
                              ? p.offerPrice
                              : p.price}
                          </strong>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Order Items Table */}
            <div className="flex-1 min-h-[200px] overflow-y-auto mb-4 border border-gray-300 rounded-lg">
              <table className="w-full min-w-[650px]">
                <thead className="sticky top-0 bg-gray-100">
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Item
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Quantity
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Note
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Remove
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {items.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-800">
                        {row.name}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => decrementQty(row.id)}
                            disabled={loading}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg
                              className="w-5 h-5 text-gray-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20 12H4"
                              />
                            </svg>
                          </button>
                          <strong className="text-sm min-w-[20px] text-center">
                            {row.quantity}
                          </strong>
                          <button
                            onClick={() => incrementQty(row.id)}
                            disabled={loading}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg
                              className="w-5 h-5 text-gray-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs font-semibold ${row.status === "queued"
                            ? "bg-yellow-200 text-yellow-800"
                            : "bg-green-200 text-green-800"
                            }`}
                        >
                          {row.status}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <input
                          type="text"
                          placeholder="Note"
                          value={row.note}
                          onChange={(e) =>
                            setItems(
                              items.map((i) =>
                                i.id === row.id
                                  ? { ...i, note: e.target.value }
                                  : i,
                              ),
                            )
                          }
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          disabled={loading || row.printedCount > 0}
                        />
                      </td>

                      <td className="px-4 py-3">
                        <button
                          onClick={() => removeItem(row.id)}
                          disabled={loading}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Billing Summary */}
            <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <strong>Subtotal:</strong>
                  <span>
                    {currencySymbol}
                    {formatAmount(subtotal).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <strong>
                    {taxLabel} ({formatTaxPercent(gstPercent)}%):
                  </strong>
                  <span>
                    {currencySymbol}
                    {formatAmount(gstAmount).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-300">
                  <strong>Grand Total:</strong>
                  <span>
                    {currencySymbol}
                    {formatAmount(grandTotal).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              <button
                onClick={() => onSaveOrder(items)}
                disabled={loading}
                className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                Save Order
              </button>
              <button
                onClick={() => setOpenSettleConfirm(true)}
                disabled={loading}
                className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                Settle Bill
              </button>
              <button
                onClick={() => setOpenCancelConfirm(true)}
                disabled={loading}
                className="w-full px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                Cancel Allocation
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Settle Bill Confirmation Dialog */}
      {openSettleConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-[90%] max-w-[400px] mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">
                Confirm Settle Bill
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Are you sure you want to settle this bill?
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setOpenSettleConfirm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSettle}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                Yes, Settle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      {openCancelConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-[90%] max-w-[400px] mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">
                Cancel Allocation
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Do you want to cancel this allocation?
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setOpenCancelConfirm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                No
              </button>
              <button
                onClick={handleConfirmCancel}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderModal;
